import { useState } from 'react';
import { useStore } from 'components/Store';
import {
  SET_REMOVE_PRODUCT_FAILURE,
  SET_TOAST_MESSAGE,
  SET_TOAST_TYPE,
  SET_SHOW_TOAST_REMOVE_PRODUCT
} from 'reducers/reducer';
import {
  product as customProductApi,
  inventoryProductPrices as listPriceApi,
  createChildApi,
  inventoryProducts
} from 'utilities/api';
import { AxiosError } from 'axios';
import { ApiResource } from 'utilities/api.types';
import { AddProductsArgs, ItemsToCreatePayload, ProductResponse } from './type';

function useInventoryProduct() {
  const { state, dispatch } = useStore();
  const orgId = state?.organization?.id;

  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState<string>('');

  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const updateListPrice = (payload: AddProductsArgs['itemsToUpdate']) =>
    (listPriceApi as ApiResource).post(
      Array.isArray(payload) ? payload : [payload],
      {
        headers: {
          'cwf-context': JSON.stringify({
            organization_id: orgId
          })
        }
      }
    ).promise;

  const addUpdateListPrice = async (
    payload: AddProductsArgs['itemsToUpdate'],
    onSuccess: () => void,
    onError: () => void
  ) => {
    setUpdateError('');
    setIsUpdateLoading(true);

    const promise = updateListPrice(payload);
    await promise
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch((err: AxiosError) => {
        setUpdateError(err.message);
        if (onError) onError();
      })
      .finally(() => {
        setIsUpdateLoading(false);
      });
  };

  const addProducts = async ({
    itemsToUpdate = [],
    itemsToCreate = []
  }: AddProductsArgs): Promise<{
    success: boolean;
    data: ProductResponse | null;
    error: AxiosError | null;
  }[]> => {
    setUpdateError('');
    setIsUpdateLoading(true);

    const promises = [];
    promises.push(updateListPrice(itemsToUpdate));
    itemsToCreate.forEach(payload => {
      promises.push((customProductApi as ApiResource).create(payload).promise);
    });

    try {
      const responses = await Promise.all(promises);
      return responses.map(response => ({
        success: true,
        data: response.data,
        error: null
      }));
    } catch (err) {
      setUpdateError((err as AxiosError).message);
      return [{ success: false, data: null, error: err as AxiosError }];
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const fetchCustomProduct = async (id: string) => {
    const { promise } = (customProductApi as ApiResource).fetch(id);
    return promise;
  };

  const updateCustomProduct = async (
    payload: { id: string } & Partial<ItemsToCreatePayload>
  ) => {
    setUpdateError('');
    setIsUpdateLoading(true);

    const updateCall = () =>
      (customProductApi as ApiResource).update(payload.id, payload, {
        headers: {
          'cwf-context': JSON.stringify({
            organization_id: orgId
          })
        }
      });

    const { promise } = updateCall();
    return promise.then(() => {
      const { promise: innerPromise } = updateCall();
      innerPromise
        .catch((err: AxiosError) => {
          setUpdateError(err.message);
        })
        .finally(() => {
          setIsUpdateLoading(false);
        });
    });
  };

  const removeProduct = async (productId: string) => {
    setRemoveError(null);
    setIsRemoveLoading(true);

    const { promise } = createChildApi(
      inventoryProducts,
      `product/${productId}/hide`
    ).patch();
    await promise
      .catch((err: AxiosError) => {
        setRemoveError(err.message);
        setIsRemoveLoading(false);
        dispatch({
          type: SET_REMOVE_PRODUCT_FAILURE,
          payload: true
        });
        dispatch({
          type: SET_TOAST_MESSAGE,
          payload: 'Unable to remove product.'
        });
        dispatch({
          type: SET_TOAST_TYPE,
          payload: 'error'
        });
        dispatch({
          type: SET_SHOW_TOAST_REMOVE_PRODUCT,
          payload: true
        });
      })
      .finally(() => {
        setIsRemoveLoading(false);
      });
  };

  return {
    fetchCustomProduct,
    addUpdateListPrice,
    addProducts,
    updateCustomProduct,
    isUpdateLoading,
    updateError,
    setUpdateError,
    removeProduct,
    isRemoveLoading,
    removeError,
    setRemoveError
  };
}

export default useInventoryProduct;
